import * as React from 'react'

import {
  FieldConfig,
  FieldType,
  LocationFieldConfig,
} from '~/client/src/shared/components/DynamicForm/types'
import SitemapAttributeIconComponent from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import { getDeletedObjectLabel } from '~/client/src/shared/utils/getDeletedObjectLabel'

import type {
  IResourceLinkConfiguration,
  ResourceLinkTargetType,
} from './targets'
import { getResourceLinkTargetType } from './targets'

export interface IResourceLinkFormatContext {
  tagsStore?: TagsStore
}

export interface IFieldBuilderBase {
  name: string
  label?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
}

export interface IResourceLinkFieldBuildContext {
  baseConfig: IFieldBuilderBase
  fieldValue: unknown
}

export interface IResourceGroupPresentation {
  // Render-context-pure: no hooks, no context reads — the registry is a config object, not a React tree.
  labelIcon?: React.ReactNode
  label?: React.ReactNode
}

export interface IResourceLinkTargetRegistration {
  targetType: ResourceLinkTargetType
  cellType: 'location'
  isFilterable: boolean
  localizedTypeLabel: () => string
  formatValue: (value: string, ctx: IResourceLinkFormatContext) => string
  buildFieldConfig: (ctx: IResourceLinkFieldBuildContext) => FieldConfig | null
  defaultPropertyConfig: () => Record<string, unknown>
  resolveGroupPresentation?: (
    args: { groupKey: string; count?: number },
    ctx: { tagsStore: TagsStore },
  ) => IResourceGroupPresentation | null
}

/**
 * The location a reference points at, deleted ones included: a child keeps naming its
 * parent after the parent is deleted, and that reference must still read as a place.
 */
export const getReferencedLocation = (
  locationId: string,
  tagsStore?: TagsStore,
): LocationBase | undefined =>
  (tagsStore?.getTagById(locationId) as LocationBase | undefined) ??
  tagsStore?.getLocationByIdWithDeleted(locationId)

/**
 * The name a location reference shows - "[DELETED] Name" once the location is deleted, so
 * a group header, filter option or cell never falls back to the raw id. Undefined when
 * the location is not known at all.
 */
export const getLocationReferenceLabel = (
  locationId: string,
  tagsStore?: TagsStore,
): string | undefined => {
  const location = getReferencedLocation(locationId, tagsStore)
  if (!location) return undefined
  return location.isDeleted
    ? getDeletedObjectLabel(location.name)
    : location.name
}

const locationRegistration: IResourceLinkTargetRegistration = {
  targetType: 'location',
  cellType: 'location',
  isFilterable: true,
  localizedTypeLabel: () => Localization.translator.resourcePropertyLocation,
  formatValue: (value, { tagsStore }) =>
    getLocationReferenceLabel(value, tagsStore) ?? value,
  buildFieldConfig: ({ baseConfig, fieldValue }) => {
    const config: LocationFieldConfig = {
      ...baseConfig,
      type: FieldType.LOCATION,
      defaultValue: fieldValue ? String(fieldValue) : undefined,
    }
    return config
  },
  defaultPropertyConfig: () => ({
    importExport: {
      entityType: 'location',
      exportField: 'name',
      importField: 'name',
    },
  }),
  resolveGroupPresentation: ({ groupKey }, { tagsStore }) => {
    const location = getReferencedLocation(groupKey, tagsStore)
    if (!location) return null
    return {
      labelIcon: <SitemapAttributeIconComponent dataObject={location} />,
      label: getLocationReferenceLabel(groupKey, tagsStore),
    }
  },
}

const registry: Record<
  ResourceLinkTargetType,
  IResourceLinkTargetRegistration
> = {
  location: locationRegistration,
}

/**
 * Returns the frontend adapter registered for a parsed resource-link target.
 */
export const getResourceLinkTargetByType = (
  targetType: ResourceLinkTargetType,
): IResourceLinkTargetRegistration => registry[targetType]

export const getResourceLinkTarget = (
  configuration?: IResourceLinkConfiguration | null,
): IResourceLinkTargetRegistration | undefined => {
  const targetType = getResourceLinkTargetType(configuration)
  if (!targetType) return undefined
  return getResourceLinkTargetByType(targetType)
}
